import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { useOrgFeatures } from '../hooks/useOrgFeatures';
import { toast } from 'sonner';
import {
  Users, TrendingUp, Zap, Package, Search, ShieldOff, Shield,
  ChevronLeft, ChevronRight, X, ExternalLink, RefreshCw, Sparkles, Layers, Building2,
  Link as LinkIcon, Copy, Check, Bot, Save, UserPlus, BarChart2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import CustomPlansTab from '../components/CustomPlansTab';
import DemoLeadsTab from '../components/DemoLeadsTab';

// ── Helpers ───────────────────────────────────────────────────
const PLAN_LABELS = {
  search_basic:  { label: 'Search Basic',  color: 'bg-slate-700 text-slate-200' },
  search_ai:     { label: 'Search AI',     color: 'bg-indigo-500/20 text-indigo-300' },
  search_ai_pro: { label: 'Search AI Pro', color: 'bg-purple-500/20 text-purple-300' },
  chat:          { label: 'Chat',          color: 'bg-emerald-500/20 text-emerald-300' },
  full:          { label: 'Full',          color: 'bg-blue-500/20 text-blue-300' },
  onboarding:    { label: 'Onboarding',    color: 'bg-amber-500/20 text-amber-300' },
};

const STATUS_COLORS = {
  active:    'bg-emerald-500/20 text-emerald-400',
  trial:     'bg-yellow-500/20 text-yellow-400',
  canceled:  'bg-red-500/20 text-red-400',
  suspended: 'bg-red-500/20 text-red-400',
  none:      'bg-gray-700 text-gray-400',
};

function StatCard({ label, value, sub, icon: Icon, color = 'text-indigo-400' }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">{label}</p>
        <Icon size={16} className={color} />
      </div>
      <p className="text-white text-2xl font-bold">{value}</p>
      {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
    </div>
  );
}

// ── Impersonation detail panel ────────────────────────────────
function ImpersonatePanel({ orgId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.post(`/api/admin/organizations/${orgId}/impersonate`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId]);

  if (loading) {
    return (
      <div className="fixed inset-y-0 right-0 w-[480px] bg-gray-950 border-l border-gray-800 z-50 flex items-center justify-center">
        <RefreshCw size={20} className="text-gray-500 animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const org = data.organization;
  const stats = data.stats;
  const recent = data.recent_searches || [];
  const feed = data.feed;
  const planInfo = PLAN_LABELS[org.plan_key] || { label: org.plan_key || 'Sin plan', color: 'bg-gray-700 text-gray-400' };

  return (
    <div className="fixed inset-y-0 right-0 w-[480px] bg-gray-950 border-l border-gray-800 z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-gray-950 border-b border-gray-800 px-6 py-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-indigo-400 font-medium uppercase tracking-wide mb-0.5">Vista cliente</p>
          <h2 className="text-white font-semibold text-base">{org.name}</h2>
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
          <X size={18} />
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* Basic info */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-gray-500 text-xs mb-1">Plan</p>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${planInfo.color}`}>
              {planInfo.label}
            </span>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-gray-500 text-xs mb-1">Estado</p>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[org.subscription_status] || STATUS_COLORS.none}`}>
              {org.subscription_status || 'none'}
            </span>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-gray-500 text-xs mb-1">Productos indexados</p>
            <p className="text-white font-bold text-lg">{stats.product_count.toLocaleString()}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-gray-500 text-xs mb-1">Búsquedas (30 días)</p>
            <p className="text-white font-bold text-lg">{stats.searches_last_30_days.toLocaleString()}</p>
          </div>
        </div>

        {/* AI Usage */}
        {org.ai_searches_limit > 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} className="text-indigo-400" />
              <p className="text-white text-sm font-medium">Sera AI este mes</p>
            </div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">Uso</span>
              <span className="text-white font-medium">
                {stats.ai_searches_used_month.toLocaleString()} / {stats.ai_searches_limit.toLocaleString()}
              </span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-500 rounded-full"
                style={{ width: `${Math.min((stats.ai_searches_used_month / stats.ai_searches_limit) * 100, 100)}%` }}
              />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${org.ai_enabled ? 'bg-emerald-400' : 'bg-gray-600'}`} />
              <p className="text-xs text-gray-400">Sera AI {org.ai_enabled ? 'activa' : 'inactiva'}</p>
            </div>
          </div>
        )}

        {/* Feed */}
        {feed && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-gray-500 text-xs mb-2">Feed XML</p>
            <p className="text-white text-xs font-mono break-all truncate">{feed.url}</p>
            <p className="text-gray-500 text-xs mt-1">
              Última sync: {feed.last_synced ? new Date(feed.last_synced).toLocaleString('es') : 'Nunca'}
            </p>
          </div>
        )}

        {/* Sinónimos */}
        {org.synonyms?.length > 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className="text-gray-500 text-xs mb-3">Sinónimos configurados</p>
            <div className="space-y-2">
              {org.synonyms.map((g, i) => (
                <div key={i} className="flex flex-wrap gap-1">
                  {g.terms.map((t, j) => (
                    <span key={j} className="bg-gray-800 text-gray-300 text-xs px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent searches */}
        {recent.length > 0 && (
          <div>
            <p className="text-gray-500 text-xs font-medium uppercase tracking-wide mb-3">Búsquedas recientes</p>
            <div className="space-y-1">
              {recent.map((s, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg">
                  <span className="text-gray-300 text-sm">{s.query}</span>
                  <span className="text-gray-600 text-xs">{s.results_count ?? 0} resultados</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* IDs */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-xs space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-500">Organization ID</span>
            <button
              onClick={() => navigator.clipboard.writeText(org.organization_id)}
              className="text-indigo-400 hover:text-indigo-300 font-mono truncate max-w-[200px]"
            >
              {org.organization_id}
            </button>
          </div>
          {org.stripe_customer_id && (
            <div className="flex justify-between">
              <span className="text-gray-500">Stripe Customer</span>
              <a
                href={`https://dashboard.stripe.com/customers/${org.stripe_customer_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
              >
                {org.stripe_customer_id} <ExternalLink size={10} />
              </a>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-500">Creado</span>
            <span className="text-gray-300">{org.created_at ? new Date(org.created_at).toLocaleDateString('es') : '—'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Suspend modal ─────────────────────────────────────────────
function SuspendModal({ org, onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-full max-w-md">
        <h3 className="text-white font-semibold mb-1">Suspender organización</h3>
        <p className="text-gray-400 text-sm mb-4">
          ¿Suspender <span className="text-white">{org.name}</span>? El cliente perderá acceso inmediatamente.
        </p>
        <input
          type="text"
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Motivo (opcional)"
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm mb-4 outline-none focus:border-red-500"
        />
        <div className="flex gap-3">
          <Button
            data-testid="confirm-suspend-btn"
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            onClick={() => onConfirm(reason)}
          >
            Suspender
          </Button>
          <Button
            variant="outline"
            className="flex-1 border-gray-700 text-gray-300"
            onClick={onCancel}
          >
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── LLM Config Tab ─────────────────────────────────────────────
function LlmConfigTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [selectedModel, setSelectedModel] = useState('gpt-4.1-mini');

  useEffect(() => {
    api.get('/api/admin/llm-config')
      .then(r => {
        setConfig(r.data);
        setSelectedProvider(r.data.provider);
        setSelectedModel(r.data.model);
      })
      .catch(() => toast.error('Error al cargar configuración LLM'))
      .finally(() => setLoading(false));
  }, []);

  const handleProviderChange = (p) => {
    setSelectedProvider(p);
    // Auto-select first model of new provider
    if (config?.available_models?.[p]?.length) {
      setSelectedModel(config.available_models[p][0].id);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/llm-config', { provider: selectedProvider, model: selectedModel });
      toast.success(`Modelo actualizado: ${selectedProvider} / ${selectedModel}`);
      setConfig(prev => ({ ...prev, provider: selectedProvider, model: selectedModel }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const PROVIDER_LABELS = { openai: 'OpenAI', anthropic: 'Anthropic', gemini: 'Google Gemini' };

  if (loading) return <div className="text-gray-500 py-8 text-center">Cargando configuración...</div>;

  return (
    <div className="max-w-xl">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-6">
        <div>
          <h3 className="text-white font-semibold text-base mb-1">Configuración global del modelo de IA</h3>
          <p className="text-gray-500 text-sm">
            El modelo seleccionado se usará en todos los módulos: chat bot, clasificador de búsqueda y mensajes de pedido.
          </p>
        </div>

        {/* Active config badge */}
        <div className="flex items-center gap-2 px-3 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
          <Bot size={14} className="text-indigo-400" />
          <span className="text-indigo-300 text-sm font-medium">
            Activo: {PROVIDER_LABELS[config?.provider] || config?.provider} / {config?.model}
          </span>
        </div>

        {/* Provider selector */}
        <div className="space-y-2">
          <label className="text-gray-400 text-xs font-medium uppercase tracking-wide">Proveedor</label>
          <div className="flex gap-2 flex-wrap">
            {Object.keys(config?.available_models || {}).map(p => (
              <button
                key={p}
                data-testid={`llm-provider-${p}`}
                onClick={() => handleProviderChange(p)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  selectedProvider === p
                    ? 'bg-indigo-600 border-indigo-500 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white hover:border-gray-600'
                }`}
              >
                {PROVIDER_LABELS[p] || p}
              </button>
            ))}
          </div>
        </div>

        {/* Model selector */}
        <div className="space-y-2">
          <label className="text-gray-400 text-xs font-medium uppercase tracking-wide">Modelo</label>
          <div className="space-y-2">
            {(config?.available_models?.[selectedProvider] || []).map(m => (
              <button
                key={m.id}
                data-testid={`llm-model-${m.id}`}
                onClick={() => setSelectedModel(m.id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-colors ${
                  selectedModel === m.id
                    ? 'bg-indigo-600/20 border-indigo-500 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600 hover:text-white'
                }`}
              >
                <span className="font-medium">{m.label}</span>
                <span className="text-xs text-gray-500 font-mono">{m.id}</span>
              </button>
            ))}
          </div>
        </div>

        <Button
          data-testid="llm-save-btn"
          onClick={handleSave}
          disabled={saving || (selectedProvider === config?.provider && selectedModel === config?.model)}
          className="w-full bg-indigo-600 hover:bg-indigo-500 text-white gap-2"
        >
          <Save size={14} />
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </Button>

        <p className="text-gray-600 text-xs">
          Asegúrate de que la API key del proveedor seleccionado está configurada en las variables de entorno del servidor.
        </p>
      </div>
    </div>
  );
}

// ── LLM Usage Tab ──────────────────────────────────────────────
function LlmUsageTab() {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((p) => {
    setLoading(true);
    api.get(`/api/admin/llm-usage?period=${p}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Error al cargar consumo LLM'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  const fmt = (n) => n?.toLocaleString('es-ES') ?? '—';
  const fmtEur = (n) => n != null ? `${n.toFixed(4)} €` : '—';

  const totals = data?.totals || {};
  const byOrg  = data?.by_org  || [];
  const byModel = data?.by_model || [];

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold text-base">Consumo de IA por organización</h3>
          <p className="text-gray-500 text-xs mt-0.5">Tokens procesados y coste estimado (precios oficiales de cada proveedor)</p>
        </div>
        <div className="flex gap-1">
          {['7d','30d','90d'].map(p => (
            <button key={p} data-testid={`llm-period-${p}`} onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                period === p ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}>{p}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500 py-12 text-center text-sm">Cargando...</div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <p className="text-gray-500 text-xs mb-1">Llamadas LLM</p>
              <p className="text-white text-xl font-bold">{fmt(totals.calls)}</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <p className="text-gray-500 text-xs mb-1">Tokens totales</p>
              <p className="text-white text-xl font-bold">{fmt(totals.total_tokens)}</p>
              <p className="text-gray-600 text-xs mt-0.5">{fmt(totals.input_tokens)} entrada · {fmt(totals.output_tokens)} salida</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <p className="text-gray-500 text-xs mb-1">Coste estimado (USD)</p>
              <p className="text-emerald-400 text-xl font-bold">{totals.cost_usd != null ? `$${totals.cost_usd.toFixed(4)}` : '—'}</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <p className="text-gray-500 text-xs mb-1">Coste estimado (EUR)</p>
              <p className="text-emerald-400 text-xl font-bold">{totals.cost_eur != null ? `${totals.cost_eur.toFixed(4)} €` : '—'}</p>
            </div>
          </div>

          {/* By model */}
          {byModel.length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wide mb-3">Por modelo</p>
              <div className="flex flex-wrap gap-2">
                {byModel.map(m => (
                  <div key={`${m.provider}-${m.model}`} className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 rounded-lg">
                    <span className="text-gray-300 text-xs font-mono">{m.model}</span>
                    <span className="text-gray-600 text-xs">·</span>
                    <span className="text-gray-400 text-xs">{fmt(m.total_tokens)} tokens</span>
                    <span className="text-gray-600 text-xs">·</span>
                    <span className="text-emerald-400 text-xs">{fmtEur(m.cost_eur)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* By org table */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-800">
              <p className="text-gray-300 text-sm font-medium">Consumo por organización</p>
            </div>
            {byOrg.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                Sin datos de consumo para el período seleccionado.
                <br/>
                <span className="text-xs text-gray-600">Los datos se generan con cada respuesta del bot en el chat.</span>
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="llm-usage-table">
                <thead>
                  <tr className="text-gray-500 text-xs border-b border-gray-800">
                    <th className="text-left px-4 py-2">Organización</th>
                    <th className="text-right px-4 py-2">Llamadas</th>
                    <th className="text-right px-4 py-2">Tokens entrada</th>
                    <th className="text-right px-4 py-2">Tokens salida</th>
                    <th className="text-right px-4 py-2">Total tokens</th>
                    <th className="text-right px-4 py-2">Coste USD</th>
                    <th className="text-right px-4 py-2">Coste EUR</th>
                    <th className="text-left px-4 py-2">Modelos</th>
                  </tr>
                </thead>
                <tbody>
                  {byOrg.map(org => (
                    <tr key={org.org_id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-white font-medium text-xs">{org.org_name}</p>
                        <p className="text-gray-600 text-xs font-mono">{org.org_id}</p>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-300 text-xs">{fmt(org.calls)}</td>
                      <td className="px-4 py-3 text-right text-gray-400 text-xs">{fmt(org.input_tokens)}</td>
                      <td className="px-4 py-3 text-right text-gray-400 text-xs">{fmt(org.output_tokens)}</td>
                      <td className="px-4 py-3 text-right text-white font-medium text-xs">{fmt(org.total_tokens)}</td>
                      <td className="px-4 py-3 text-right text-emerald-400 text-xs">${org.cost_usd?.toFixed(4) ?? '—'}</td>
                      <td className="px-4 py-3 text-right text-emerald-400 font-medium text-xs">{fmtEur(org.cost_eur)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(org.models || []).map(m => (
                            <span key={m} className="px-1.5 py-0.5 bg-gray-800 text-gray-400 text-xs rounded font-mono">{m}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <p className="text-gray-600 text-xs">
            * Coste estimado basado en precios oficiales de cada proveedor (tokens contados con litellm). Tasa EUR/USD fija: 0.92. Los datos comienzan a acumularse a partir de ahora.
          </p>
        </>
      )}
    </div>
  );
}

// ── Main AdminPage ─────────────────────────────────────────────
export default function AdminPage() {
  const { isAdmin } = useOrgFeatures();
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [impersonateOrgId, setImpersonateOrgId] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [plans, setPlans] = useState([]);
  const [actionMsg, setActionMsg] = useState('');
  const [activeTab, setActiveTab] = useState('orgs'); // 'orgs' | 'plans' | 'llm' | 'llm-usage' | 'leads'
  const [checkoutModal, setCheckoutModal] = useState(null); // {url, orgName}
  const [copiedLink, setCopiedLink] = useState(false);
  const [linkLoading, setLinkLoading] = useState(null); // org_id

  const handleGenerateLink = async (org) => {
    if (!org.plan_key) {
      toast.error('Selecciona primero un plan para esta organización');
      return;
    }
    setLinkLoading(org.organization_id);
    try {
      const r = await api.post(`/api/admin/organizations/${org.organization_id}/checkout-link`, {
        plan_key: org.plan_key,
        origin_url: window.location.origin,
      });
      setCheckoutModal({ url: r.data.url, orgName: org.name });
      setCopiedLink(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar el enlace');
    } finally {
      setLinkLoading(null);
    }
  };

  const copyLink = () => {
    if (!checkoutModal?.url) return;
    navigator.clipboard.writeText(checkoutModal.url).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    });
  };

  const loadAll = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [statsRes, orgsRes, plansRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get(`/api/admin/organizations?page=${page}&limit=20${search ? `&search=${encodeURIComponent(search)}` : ''}`),
        api.get('/api/admin/plans?include_hidden=true'),
      ]);
      setStats(statsRes.data);
      setOrgs(orgsRes.data.organizations || []);
      setTotal(orgsRes.data.total || 0);
      // For the org plan dropdown: include hidden + custom plans (admin only) but exclude add-ons
      setPlans(plansRes.data.plans?.filter(p => !p.is_addon && p.is_active !== false) || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, search]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handlePlanChange = async (orgId, planKey) => {
    if (!planKey) return;
    try {
      await api.put(`/api/admin/organizations/${orgId}/plan`, {
        plan_key: planKey,
        subscription_status: 'active',
      });
      flashMsg('Plan actualizado');
      loadAll();
    } catch (e) {
      flashMsg(e.response?.data?.detail || 'Error al cambiar plan', true);
    }
  };

  const handleToggleStatus = async (org) => {
    if (org.is_active !== false) {
      // Suspend → show confirmation modal
      setSuspendTarget(org);
    } else {
      // Reactivate directly
      await doStatusUpdate(org.organization_id, true, '');
    }
  };

  const doStatusUpdate = async (orgId, isActive, reason) => {
    try {
      await api.put(`/api/admin/organizations/${orgId}/status`, { is_active: isActive, reason });
      flashMsg(isActive ? 'Organización reactivada' : 'Organización suspendida');
      setSuspendTarget(null);
      loadAll();
    } catch (e) {
      flashMsg('Error al cambiar estado', true);
    }
  };

  const flashMsg = (msg, isError = false) => {
    setActionMsg({ text: msg, error: isError });
    setTimeout(() => setActionMsg(''), 3000);
  };

  if (!isAdmin) {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-96">
        <ShieldOff size={40} className="text-gray-600 mb-4" />
        <h2 className="text-white text-xl font-semibold">Acceso restringido</h2>
        <p className="text-gray-400 mt-2">Solo los administradores pueden acceder a este panel.</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Checkout link modal */}
      {checkoutModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-lg shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-white font-semibold text-lg">Enlace de pago</h3>
                <p className="text-gray-400 text-sm mt-0.5">
                  Envía este enlace a <span className="text-white font-medium">{checkoutModal.orgName}</span>
                </p>
              </div>
              <button onClick={() => setCheckoutModal(null)} className="text-gray-500 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 mb-4">
              <p className="text-indigo-300 text-xs font-mono break-all flex-1 select-all">
                {checkoutModal.url}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={copyLink}
                data-testid="copy-checkout-link-btn"
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  copiedLink
                    ? 'bg-emerald-600 text-white'
                    : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                }`}
              >
                {copiedLink ? <Check size={15} /> : <Copy size={15} />}
                {copiedLink ? '¡Copiado!' : 'Copiar enlace'}
              </button>
              <a
                href={checkoutModal.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
              >
                <ExternalLink size={15} /> Abrir
              </a>
            </div>

            <p className="text-gray-600 text-xs mt-3 text-center">
              El enlace caduca en 24h. Genera uno nuevo si es necesario.
            </p>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
          <p className="text-gray-500 text-sm mt-0.5">Panel interno EcommSystem</p>
        </div>
        <button
          onClick={loadAll}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
          data-testid="admin-refresh-btn"
        >
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* Action toast */}
      {actionMsg && (
        <div className={`mb-5 px-4 py-3 rounded-xl text-sm font-medium border ${
          actionMsg.error
            ? 'bg-red-500/10 border-red-500/30 text-red-300'
            : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
        }`}>
          {actionMsg.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-gray-800">
        <button
          data-testid="admin-tab-orgs"
          onClick={() => setActiveTab('orgs')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'orgs'
              ? 'border-indigo-500 text-white'
              : 'border-transparent text-gray-500 hover:text-gray-300'
          }`}
        >
          <Building2 size={14} /> Organizaciones
        </button>
        <button
          data-testid="admin-tab-plans"
          onClick={() => setActiveTab('plans')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'plans'
              ? 'border-indigo-500 text-white'
              : 'border-transparent text-gray-500 hover:text-gray-300'
          }`}
        >
          <Layers size={14} /> Planes
        </button>
        <button
          data-testid="admin-tab-llm"
          onClick={() => setActiveTab('llm')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'llm'
              ? 'border-indigo-500 text-white'
              : 'border-transparent text-gray-500 hover:text-gray-300'
          }`}
        >
          <Bot size={14} /> Modelo IA
        </button>
        <button
          data-testid="admin-tab-leads"
          onClick={() => setActiveTab('leads')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'leads'
              ? 'border-indigo-500 text-white'
              : 'border-transparent text-gray-500 hover:text-gray-300'
          }`}
        >
          <UserPlus size={14} /> Leads (Landing)
        </button>
        <button
          data-testid="admin-tab-llm-usage"
          onClick={() => setActiveTab('llm-usage')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'llm-usage'
              ? 'border-indigo-500 text-white'
              : 'border-transparent text-gray-500 hover:text-gray-300'
          }`}
        >
          <BarChart2 size={14} /> Consumo IA
        </button>
      </div>

      {activeTab === 'llm' ? (
        <LlmConfigTab />
      ) : activeTab === 'plans' ? (
        <CustomPlansTab />
      ) : activeTab === 'leads' ? (
        <DemoLeadsTab />
      ) : activeTab === 'llm-usage' ? (
        <LlmUsageTab />
      ) : (
      <>
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="MRR" value={`${stats.mrr.toLocaleString()}€`} icon={TrendingUp} color="text-emerald-400"
            sub={Object.entries(stats.plan_breakdown || {}).map(([k, v]) => `${v} ${k}`).join(' · ') || '—'} />
          <StatCard label="Suscripciones activas" value={stats.active_subscriptions} icon={Users} color="text-indigo-400"
            sub={`${stats.trial_organizations} en trial`} />
          <StatCard label="Búsquedas AI este mes" value={(stats.total_ai_searches_month || 0).toLocaleString()} icon={Sparkles} color="text-purple-400" />
          <StatCard label="Productos indexados" value={(stats.total_products_indexed || 0).toLocaleString()} icon={Package} color="text-blue-400"
            sub={`${stats.monthly_searches || 0} búsquedas clásicas`} />
        </div>
      )}

      {/* Organizations table */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        {/* Search bar */}
        <div className="p-4 border-b border-gray-800 flex items-center gap-3">
          <div className="flex-1 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              data-testid="admin-search-input"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar por nombre o ID..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-8 pr-4 py-2 text-white text-sm placeholder-gray-500 outline-none focus:border-indigo-500"
            />
          </div>
          <span className="text-gray-500 text-sm">{total} organizaciones</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/50">
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Organización</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Plan</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Estado</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Búsquedas AI</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Productos</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-800/50">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-4">
                        <div className="h-4 bg-gray-800 rounded animate-pulse w-24" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : orgs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-500">
                    No hay organizaciones
                  </td>
                </tr>
              ) : orgs.map(org => {
                const planInfo = PLAN_LABELS[org.plan_key] || { label: org.plan_key || 'Sin plan', color: 'bg-gray-700 text-gray-400' };
                const isActive = org.is_active !== false;
                const aiLimit = org.ai_searches_limit || 0;
                const aiUsed = org.ai_searches_used_month || 0;
                const aiPct = aiLimit > 0 ? Math.min((aiUsed / aiLimit) * 100, 100) : 0;

                return (
                  <tr
                    key={org.organization_id}
                    data-testid="admin-org-row"
                    className="border-b border-gray-800/50 hover:bg-gray-800/20 transition-colors"
                  >
                    {/* Name */}
                    <td className="px-4 py-3">
                      <p className="text-white font-medium">{org.name}</p>
                      <p className="text-gray-600 text-xs font-mono">{org.organization_id.slice(0, 20)}…</p>
                    </td>

                    {/* Plan selector */}
                    <td className="px-4 py-3">
                      <select
                        data-testid="admin-plan-select"
                        value={org.plan_key || ''}
                        onChange={e => handlePlanChange(org.organization_id, e.target.value)}
                        className="bg-gray-800 border border-gray-700 text-gray-300 text-xs rounded-lg px-2 py-1.5 outline-none focus:border-indigo-500"
                      >
                        <option value="">Sin plan</option>
                        {plans.map(p => (
                          <option key={p.plan_key} value={p.plan_key}>
                            {p.is_custom ? '★ ' : ''}{p.name} — {p.price}€{p.is_hidden ? ' (oculto)' : ''}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        !isActive ? 'bg-red-500/20 text-red-400' :
                        STATUS_COLORS[org.subscription_status] || STATUS_COLORS.none
                      }`}>
                        {!isActive ? 'Suspendida' : (org.subscription_status || 'none')}
                      </span>
                    </td>

                    {/* AI Usage */}
                    <td className="px-4 py-3">
                      {aiLimit > 0 ? (
                        <div className="min-w-[100px]">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-400">{aiUsed.toLocaleString()}</span>
                            <span className="text-gray-600">{aiLimit.toLocaleString()}</span>
                          </div>
                          <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${aiPct >= 100 ? 'bg-red-500' : aiPct >= 80 ? 'bg-amber-500' : 'bg-indigo-500'}`}
                              style={{ width: `${aiPct}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-600 text-xs">—</span>
                      )}
                    </td>

                    {/* Products */}
                    <td className="px-4 py-3 text-gray-300 text-sm">
                      {(org.product_count || 0).toLocaleString()}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {/* Generate checkout link for hidden/custom plan */}
                        <button
                          data-testid="generate-checkout-link-btn"
                          onClick={() => handleGenerateLink(org)}
                          disabled={linkLoading === org.organization_id}
                          className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1 transition-colors disabled:opacity-50"
                          title="Generar enlace de pago para el plan seleccionado"
                        >
                          <LinkIcon size={12} />
                          {linkLoading === org.organization_id ? '...' : 'Enlace pago'}
                        </button>

                        {/* Impersonate */}
                        <button
                          data-testid="impersonate-btn"
                          onClick={() => setImpersonateOrgId(org.organization_id)}
                          className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 transition-colors"
                          title="Ver como cliente"
                        >
                          <ExternalLink size={12} /> Ver
                        </button>

                        {/* Suspend / Activate */}
                        <button
                          data-testid="toggle-status-btn"
                          onClick={() => handleToggleStatus(org)}
                          className={`text-xs flex items-center gap-1 transition-colors ${
                            isActive
                              ? 'text-red-400 hover:text-red-300'
                              : 'text-emerald-400 hover:text-emerald-300'
                          }`}
                          title={isActive ? 'Suspender' : 'Activar'}
                        >
                          {isActive ? <ShieldOff size={12} /> : <Shield size={12} />}
                          {isActive ? 'Suspender' : 'Activar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 20 && (
          <div className="flex justify-between items-center px-4 py-3 border-t border-gray-800">
            <span className="text-gray-500 text-sm">
              {((page - 1) * 20) + 1}–{Math.min(page * 20, total)} de {total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 text-sm disabled:opacity-40 hover:bg-gray-700 transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page * 20 >= total}
                className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 text-sm disabled:opacity-40 hover:bg-gray-700 transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Impersonate slide panel */}
      {impersonateOrgId && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setImpersonateOrgId(null)}
          />
          <ImpersonatePanel
            orgId={impersonateOrgId}
            onClose={() => setImpersonateOrgId(null)}
          />
        </>
      )}

      {/* Suspend confirmation modal */}
      {suspendTarget && (
        <SuspendModal
          org={suspendTarget}
          onConfirm={reason => doStatusUpdate(suspendTarget.organization_id, false, reason)}
          onCancel={() => setSuspendTarget(null)}
        />
      )}
      </>
      )}
    </div>
  );
}
